import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { apiRequest } from '../modules/shared/hook/useApi';
import { IUser } from '../types';
import { LikeMindedState } from './store';

type AuthState = 'idle' | 'logged_in' | 'logged_out';
interface UserSlice {
  loading: boolean;
  status: AuthState;
  user?: IUser;
}

const initialState = {
  loading: true,
  authenticated: false,
  status: 'idle',
} as UserSlice;

export const fetchUser = createAsyncThunk<
  IUser,
  void,
  {
    rejectValue: number;
  }
>('user/profile', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest<IUser>('getAuthUser');
    return response;
  } catch (e) {
    Sentry.captureException(e);
    return rejectWithValue(401);
  }
});

export const patchUser = createAsyncThunk<
  IUser,
  Partial<IUser>,
  {
    rejectValue: number;
  }
>(
  'user/update',
  async (
    {
      marketingConsent,
      recommendationsConsent,
      marketingReminderDismissed,
      locale,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiRequest<IUser>('updateUser', {
        locale,
        marketingConsent,
        marketingReminderDismissed,
        recommendationsConsent,
      });

      return response;
    } catch (e) {
      Sentry.captureException(e);
      return rejectWithValue(401);
    }
  },
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(patchUser.fulfilled, (state, action) => {
      state.user = { ...state.user, ...action.payload };
    });
    builder.addCase(fetchUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.status = 'logged_in';
    });
    builder.addCase(fetchUser.rejected, (state) => {
      state.loading = false;
      state.status = 'logged_out';
    });
  },
});

export const userReducer = userSlice.reducer;
export const selectUser = (state: LikeMindedState) => state.user;
